import { ThemeEvents } from '@theme/events';

/**
 * Normalizes a string for loose parent/child filter matching.
 * @param {string} value
 * @returns {string}
 */
function normalizeKey(value) {
  return value.toLowerCase().replace(/[\s\-_.]/g, '');
}

/**
 * Returns the last segment of a facet param name (e.g. skin_type).
 * @param {string} paramName
 * @returns {string}
 */
function paramTailKey(paramName) {
  const tail = paramName.split('.').pop() || '';
  return normalizeKey(tail);
}

/**
 * Syncs the mobile products-count (outside the grid) from the morph'd facets count.
 */
function syncMobileProductsCount() {
  const mobileCount = document.querySelector('[data-products-count-text]');
  if (!(mobileCount instanceof HTMLElement)) return;

  const source =
    document.querySelector('[data-testid="products-count"] .status') ||
    document.querySelector('[data-testid="products-count"] [role="status"]') ||
    document.querySelector('[data-testid="products-count"]');

  if (!(source instanceof HTMLElement)) return;

  const nextText = source.textContent?.trim();
  if (nextText) mobileCount.textContent = nextText;
}

/**
 * @param {HTMLInputElement} input
 * @returns {string[]}
 */
function getInputMatchKeys(input) {
  const keys = [input.value, input.getAttribute('aria-label'), input.dataset.label];

  const label = input.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
  if (label) keys.push(label.textContent);

  return keys.filter(Boolean).map((key) => normalizeKey(String(key)));
}

class PlpQuickFiltersComponent extends HTMLElement {
  /** @type {number | null} */
  #syncTimeout = null;

  /** @type {MutationObserver | null} */
  #morphObserver = null;

  /** @type {number | null} */
  #readyRetryTimeout = null;

  connectedCallback() {
    this.#bindEvents();
    this.#syncActiveStates({ updateDisabled: false });
    this.#scheduleFacetsReadySync();
  }

  disconnectedCallback() {
    this.#unbindEvents();
    this.#cancelPendingSync();
  }

  #bindEvents() {
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #unbindEvents() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #cancelPendingSync() {
    if (this.#syncTimeout) {
      window.clearTimeout(this.#syncTimeout);
      this.#syncTimeout = null;
    }
    if (this.#readyRetryTimeout) {
      window.clearTimeout(this.#readyRetryTimeout);
      this.#readyRetryTimeout = null;
    }
    this.#morphObserver?.disconnect();
    this.#morphObserver = null;
  }

  /**
   * Re-sync disabled/active from live facet inputs once the facets form exists.
   * Liquid SSR can leave pills with data-filter-disabled but no param wiring.
   */
  #scheduleFacetsReadySync() {
    const trySync = () => {
      if (!this.#hasFacetInputs()) return false;
      this.#syncActiveStates({ updateDisabled: true });
      return true;
    };

    if (trySync()) return;

    let attempts = 0;
    const maxAttempts = 20;
    const poll = () => {
      attempts += 1;
      if (trySync() || attempts >= maxAttempts) return;
      this.#readyRetryTimeout = window.setTimeout(poll, 100);
    };

    this.#readyRetryTimeout = window.setTimeout(poll, 100);
  }

  /**
   * @returns {string | null}
   */
  #getFacetsSectionId() {
    const resultsList = document.querySelector('results-list[section-id]');
    return resultsList?.getAttribute('section-id') ?? null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getFacetsSection() {
    const sectionId = this.#getFacetsSectionId();
    if (!sectionId) return null;
    return document.getElementById(`shopify-section-${sectionId}`);
  }

  /**
   * @returns {HTMLFormElement[]}
   */
  #getFacetForms() {
    const sectionId = this.#getFacetsSectionId();
    const forms = [];

    if (sectionId) {
      const desktop = document.getElementById(`FacetFiltersForm--${sectionId}-desktop`);
      const overflow = document.getElementById(`FacetFiltersForm--${sectionId}-overflow`);
      if (desktop instanceof HTMLFormElement) forms.push(desktop);
      if (overflow instanceof HTMLFormElement) forms.push(overflow);
    }

    if (forms.length === 0) {
      for (const form of document.querySelectorAll('.facets__form')) {
        if (form instanceof HTMLFormElement) forms.push(form);
      }
    }

    return forms;
  }

  /**
   * @returns {boolean}
   */
  #hasFacetInputs() {
    return this.#getFacetForms().some((form) => form.querySelector('input[type="checkbox"]'));
  }

  /**
   * @param {string} paramName
   * @param {string} paramValue
   * @returns {HTMLInputElement[]}
   */
  #findFilterInputsByParam(paramName, paramValue) {
    const inputs = [];

    for (const form of this.#getFacetForms()) {
      const selector = `input[type="checkbox"][name="${CSS.escape(paramName)}"][value="${CSS.escape(paramValue)}"]`;
      const input = form.querySelector(selector);
      if (input instanceof HTMLInputElement) inputs.push(input);
    }

    return inputs;
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} parentKey
   * @returns {boolean}
   */
  #panelMatchesParent(panel, parentKey) {
    const label = panel.querySelector('.facets__summary .facets__label');
    if (label instanceof HTMLElement && normalizeKey(label.textContent || '') === parentKey) {
      return true;
    }

    const namedInput = panel.querySelector('input[type="checkbox"][name]');
    if (namedInput instanceof HTMLInputElement) {
      return paramTailKey(namedInput.name) === parentKey;
    }

    return false;
  }

  /**
   * @param {string} parent
   * @param {string} child
   * @returns {HTMLInputElement[]}
   */
  #findFilterInputsByLabel(parent, child) {
    const parentKey = normalizeKey(parent);
    const childKey = normalizeKey(child);
    const inputs = [];

    for (const form of this.#getFacetForms()) {
      for (const panel of form.querySelectorAll('.facets__panel')) {
        if (!(panel instanceof HTMLElement)) continue;
        if (!this.#panelMatchesParent(panel, parentKey)) continue;

        for (const input of panel.querySelectorAll('input[type="checkbox"]')) {
          if (!(input instanceof HTMLInputElement)) continue;
          if (getInputMatchKeys(input).includes(childKey)) inputs.push(input);
        }
      }
    }

    return inputs;
  }

  /**
   * Persist resolved facet wiring onto the pill for later clicks/syncs.
   * @param {HTMLButtonElement} button
   * @param {HTMLInputElement} input
   */
  #hydrateFilterData(button, input) {
    if (!button.dataset.filterParam) button.dataset.filterParam = input.name;
    if (!button.dataset.filterValue) button.dataset.filterValue = input.value;
  }

  /**
   * @param {HTMLButtonElement} button
   * @returns {HTMLInputElement[]}
   */
  #resolveFilterInputs(button) {
    const paramName = button.dataset.filterParam;
    const paramValue = button.dataset.filterValue;

    if (paramName && paramValue) {
      const inputs = this.#findFilterInputsByParam(paramName, paramValue);
      if (inputs.length > 0) return inputs;
    }

    const parent = button.dataset.parent;
    const child = button.dataset.child;
    if (!parent || !child) return [];

    const inputs = this.#findFilterInputsByLabel(parent, child);
    if (inputs[0]) this.#hydrateFilterData(button, inputs[0]);
    return inputs;
  }

  /**
   * Facet checkboxes are the source of truth for availability after morph.
   * Orphan pills (no matching input once facets exist) stay disabled.
   * @param {HTMLInputElement[]} inputs
   * @param {HTMLButtonElement} button
   * @returns {boolean}
   */
  #isFilterDisabled(inputs, button) {
    if (inputs.length > 0) {
      return inputs.every((input) => input.disabled);
    }

    if (this.#hasFacetInputs()) return true;

    return button.dataset.filterDisabled === 'true';
  }

  /**
   * Toggles a facet checkbox and runs the facet update handler
   * (same path as facet-inputs-component change / keyboard activate).
   * @param {HTMLInputElement} input
   * @param {HTMLInputElement[]} [siblings]
   */
  #applyFacetSelection(input, siblings = []) {
    const nextChecked = !input.checked;

    for (const sibling of siblings) {
      if (!sibling.disabled) sibling.checked = nextChecked;
    }
    input.checked = nextChecked;

    const facetInputs = input.closest('facet-inputs-component');
    if (facetInputs && typeof facetInputs.updateFilters === 'function') {
      facetInputs.updateFilters();
      return;
    }

    const facetsForm = input.closest('facets-form-component');
    if (facetsForm && typeof facetsForm.updateFilters === 'function') {
      facetsForm.updateFilters();
    }
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-parent][data-child]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled) return;

    const inputs = this.#resolveFilterInputs(button);
    const input = inputs.find((candidate) => !candidate.disabled) ?? inputs[0];
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    this.#applyFacetSelection(input, inputs);
  };

  /**
   * filter:update fires before Section Rendering morphs main-collection.
   * Syncing against the pre-morph form leaves pills stuck disabled after clear.
   * Wait for the morph, then sync from the new facet inputs.
   */
  #handleFilterUpdate = () => {
    this.#cancelPendingSync();

    const section = this.#getFacetsSection();
    const runSync = () => {
      this.#cancelPendingSync();
      this.#syncActiveStates({ updateDisabled: true });
      syncMobileProductsCount();
    };

    if (!section) {
      this.#syncTimeout = window.setTimeout(runSync, 300);
      return;
    }

    // INP risk: low — observer only schedules a debounced attribute sync; no layout reads.
    this.#morphObserver = new MutationObserver(() => {
      if (!this.#hasFacetInputs()) return;

      if (this.#syncTimeout) window.clearTimeout(this.#syncTimeout);
      this.#syncTimeout = window.setTimeout(runSync, 100);
    });

    this.#morphObserver.observe(section, { childList: true, subtree: true });

    this.#syncTimeout = window.setTimeout(runSync, 5000);
  };

  /**
   * @param {HTMLInputElement} input
   * @returns {boolean}
   */
  #isInputActiveInUrl(input) {
    const params = new URLSearchParams(window.location.search);
    return params.getAll(input.name).includes(input.value);
  }

  /**
   * @param {Element} el
   * @param {string} className
   * @param {boolean} enabled
   */
  #setClass(el, className, enabled) {
    if (enabled) {
      el.classList.add(className);
    } else {
      el.classList.remove(className);
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} isDisabled
   */
  #setDisabledState(button, isDisabled) {
    button.disabled = isDisabled;
    this.#setClass(button, 'plp-quick-filters__pill--disabled', isDisabled);
    button.setAttribute('aria-disabled', String(isDisabled));
    button.dataset.filterDisabled = String(isDisabled);
  }

  /**
   * @param {{ updateDisabled?: boolean }} [options]
   */
  #syncActiveStates(options = {}) {
    const { updateDisabled = true } = options;
    const buttons = this.querySelectorAll('[data-parent][data-child]');

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;

      const inputs = this.#resolveFilterInputs(button);
      const isDisabled = updateDisabled
        ? this.#isFilterDisabled(inputs, button)
        : button.disabled || button.dataset.filterDisabled === 'true';
      const input = inputs.find((candidate) => !candidate.disabled) ?? inputs[0];

      let isActive = false;
      if (!isDisabled && input instanceof HTMLInputElement) {
        isActive = this.#isInputActiveInUrl(input);
      } else if (!updateDisabled) {
        isActive = button.classList.contains('plp-quick-filters__pill--active');
      }

      if (updateDisabled) {
        this.#setDisabledState(button, isDisabled);
      }

      this.#setClass(button, 'plp-quick-filters__pill--active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    }
  }
}

if (!customElements.get('plp-quick-filters-component')) {
  customElements.define('plp-quick-filters-component', PlpQuickFiltersComponent);
}

// Keep mobile count in sync even when quick-filter pills are absent
document.addEventListener(ThemeEvents.FilterUpdate, () => {
  window.setTimeout(syncMobileProductsCount, 300);
});
